import * as Scrivito from "scrivito";
import { create } from "zustand";
import type { Client } from "urql";
import { persist } from "zustand/middleware";
import { login, logout, refresh } from "api/ABrest/ABrest";
import { UserDataQryStr } from "api/queries";
import { getHomepage, getPageObjAsync } from "utils";
import {
  MAX_REFRESH_ATTEMPTS,
  sessionStorageDisabledConfirmationsKey,
} from "variables";
import { createSelectors } from "./createSelectors";
import {
  resolveFlags,
  type FeatureFlagsReadonly,
} from "utils/featureFlags/resolveFlags/resolveFlags";
import { fetchData } from "hooks";

interface CurrentUser {
  id: string;
  fullName: string;
  email: string;
  tenant: string;
  currencyCode: string;
  canSeePrices: boolean;
}

interface UserDepartment {
  value: string;
  label: string;
}
interface AuthState {
  currentUser: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | string | null;
  department: UserDepartment | null;
  urqlClient: Client | null;
  featureFlags: FeatureFlagsReadonly | null;
}

interface AuthActions {
  setUrqlClient: (data: Client | null) => void;
  setDepartment: (department: UserDepartment | null) => void;
  redirectToLoginPage: () => Promise<void>;
  redirectToWelcomePage: () => Promise<void>;
  fetchUserData: () => Promise<{ currentUser: CurrentUser }>;
  login: (email: string, password: string) => Promise<Response>;
  logout: () => Promise<void>;
  refresh: (
    retryDelay?: number,
    attempt?: number,
  ) => Promise<boolean | undefined>;
}

type AuthStoreState = AuthState & { actions: AuthActions };

const initialState = {
  currentUser: null,
  isAuthenticated: false,
  isLoading: false,
  department: null,
  error: null,
  urqlClient: null,
  featureFlags: null,
};

const useAuthStoreBase = create<AuthStoreState>()(
  persist(
    (set, get) => ({
      ...initialState,

      actions: {
        setUrqlClient: (data: Client | null) => set({ urqlClient: data }),
        setDepartment: (department: UserDepartment | null) =>
          set({ department }),
        redirectToLoginPage: async (): Promise<void> => {
          try {
            const res = await getPageObjAsync("LoginPage");
            if (!res) {
              console.error("Login page not found");
              return;
            }
            // not Scrivito.navigate because urqlClient needs full reload
            // see urql issue with clear store: https://github.com/urql-graphql/urql/issues/2511
            window.location.href = `${window.location.origin}/${res.id()}`;
          } catch (err) {
            console.error(err);
          }
        },
        redirectToWelcomePage: async (): Promise<void> => {
          const page = await Scrivito.load(() => {
            const homepage = getHomepage();
            const welcomePage = homepage?.get("welcomePage");

            if (welcomePage instanceof Scrivito.Obj) {
              return welcomePage;
            } else if (homepage instanceof Scrivito.Obj) {
              return homepage;
            }

            return null;
          });

          if (page) {
            Scrivito.navigateTo(page);
          }
        },

        fetchUserData: async (): Promise<{ currentUser: CurrentUser }> => {
          set({ isLoading: true });
          await Promise.resolve(); // ensure this runs after any pending state updates
          const promise = new Promise<{ currentUser: CurrentUser }>(
            (resolve, reject) => {
              fetchData({
                query: UserDataQryStr,
                variables: {},
                onSuccess: (data) => {
                  set({
                    currentUser: data.data.currentUser,
                    isLoading: false,
                    featureFlags: resolveFlags(data.data.currentUser.tenant),
                    isAuthenticated: true,
                  });
                  resolve({ currentUser: data.data.currentUser });
                },
                onError: (error) => {
                  console.error("Failed to fetch user data:", error);
                  set({
                    error: error.message,
                    isLoading: false,
                    isAuthenticated: false,
                  });
                  reject(error);
                },
              });
            },
          );
          return promise;
        },
        // LOGIN
        login: async (email: string, password: string): Promise<Response> => {
          set({ isLoading: true, error: null });
          try {
            const res = await login(email, password);

            if (!res.ok) {
              throw new Error(`HTTP error! Status: ${res.status}`);
            }

            const userData = await get().actions.fetchUserData();

            set({
              isAuthenticated: true,
              isLoading: false,
              currentUser: userData.currentUser,
              featureFlags: resolveFlags(userData.currentUser.tenant),
            });
            await get().actions.redirectToWelcomePage();

            return res;
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            set({ error: errorMessage, isLoading: false });
            throw error;
          }
        },

        // LOGOUT
        logout: async (): Promise<void> => {
          set({ isLoading: true });
          try {
            const res = await logout();

            if (res.ok) {
              sessionStorage.removeItem(sessionStorageDisabledConfirmationsKey);
              set(initialState);

              await get().actions.redirectToLoginPage();
            } else {
              throw new Error(`HTTP error! Status: ${res.status}`);
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            set({ error: errorMessage, isLoading: false });
            throw error;
          }
        },

        // REFRESH
        refresh: async (
          retryDelay: number = 0,
          attempt: number = 1,
        ): Promise<boolean | undefined> => {
          try {
            const response = await refresh();

            if (response.status !== 200) {
              sessionStorage.removeItem(sessionStorageDisabledConfirmationsKey);
              set(initialState);
              await get().actions.redirectToLoginPage();
              return false;
            }

            if (response.status === 200) {
              return true;
            }
          } catch (err) {
            console.error(err);
            if (attempt >= MAX_REFRESH_ATTEMPTS) {
              set(initialState);
              await get().actions.redirectToLoginPage();
              return false;
            }

            const nextRetryDelay = retryDelay ? retryDelay * 2 : 1000;

            return get().actions.refresh(nextRetryDelay, attempt + 1);
          }
        },
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        currentUser: state.currentUser,
        isAuthenticated: state.isAuthenticated,
        department: state.department,
      }),
    },
  ),
);

export const useAuthStore = createSelectors(useAuthStoreBase);
export const getFeatureFlags = () => useAuthStoreBase.getState().featureFlags;
